import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpen, ArrowRight, TrendingUp, Shield, Award } from 'lucide-react';

const GuidesPage = () => {
  const guides = [
    {
      id: 'choosing-right-casino',
      title: 'Choosing the Right Casino',
      description: 'Master the art of selecting a casino that matches your playing style, budget, and preferences.',
    },
    {
      id: 'understanding-welcome-bonuses',
      title: 'Understanding Welcome Bonuses',
      description: 'Decode bonus terms, maximize value, and avoid common pitfalls when claiming welcome offers.',
    },
    {
      id: 'wagering-requirements-explained',
      title: 'Wagering Requirements Explained',
      description: 'Learn how playthrough requirements work and find casinos with the fairest bonus terms.',
    },
    {
      id: 'payment-methods-guide',
      title: 'Payment Methods Guide',
      description: 'Compare deposit and withdrawal options, processing times, and fees across UK casinos.',
    },
    {
      id: 'responsible-gambling',
      title: 'Responsible Gambling',
      description: 'Essential tools and strategies to maintain control and enjoy casino gaming safely.',
    },
    {
      id: 'slot-game-strategies',
      title: 'Slot Game Strategies',
      description: 'Proven tips for maximizing wins, understanding RTP, and choosing the best slot games.',
    },
    {
      id: 'table-games-guide',
      title: 'Table Games Guide',
      description: 'Master blackjack, roulette, baccarat, and poker with expert strategies and betting systems.',
    },
    {
      id: 'withdrawal-process',
      title: 'Withdrawal Process',
      description: 'Navigate cashout procedures, understand verification requirements, and speed up payouts.',
    },
    {
      id: 'casino-security-licensing',
      title: 'Casino Security & Licensing',
      description: 'Verify operator legitimacy, understand encryption, and protect your personal information.',
    },
  ];

  // Chart data for visualization
  const chart1Data = [
    { label: 'Licensed Casinos', value: 95, color: 'bg-yellow-accent' },
    { label: 'Unlicensed', value: 5, color: 'bg-gray-400' },
  ];

  const chart2Data = [
    { label: 'E-wallets', value: 24, color: 'bg-yellow-accent' },
    { label: 'Bank Transfer', value: 72, color: 'bg-gray-400' },
    { label: 'Cryptocurrency', value: 48, color: 'bg-gray-500' },
  ];

  const chart3Data = [
    { label: 'Slots', value: 85, color: 'bg-yellow-accent' },
    { label: 'Table Games', value: 60, color: 'bg-gray-400' },
    { label: 'Live Casino', value: 45, color: 'bg-gray-500' },
    { label: 'Other', value: 20, color: 'bg-gray-600' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-dark to-gray-medium text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <BookOpen className="text-yellow-accent" size={40} />
              <h1 className="text-4xl md:text-5xl font-bold">Casino Guides</h1>
            </div>
            <p className="text-xl text-gray-200 leading-relaxed">
              Expert insights to help you play smarter, win more, and navigate the UK online casino landscape with confidence.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-dark mb-6">How We Write Our Guides</h2>
            
            <div className="prose prose-lg max-w-none text-gray-light leading-relaxed space-y-6">
              <p>
                Every guide you read on Sceptre Slots starts with real-world testing. We don't just compile information 
                from other sources – we dive deep into each casino, test every feature, verify every claim, and experience 
                the platform exactly as you would. Our team spends hundreds of hours playing, depositing, withdrawing, 
                and analyzing every aspect of the UK casino landscape.
              </p>
              
              <p>
                When we write about bonuses, we've claimed them ourselves. When we discuss withdrawal speeds, we've 
                processed real cashouts. When we review game libraries, we've spun the reels and placed the bets. 
                This hands-on approach means our guides reflect actual player experiences, not marketing copy or 
                theoretical knowledge.
              </p>
              
              <p>
                Our methodology combines quantitative analysis with qualitative insights. We track payout percentages, 
                measure response times, calculate bonus values, and compare terms across dozens of operators. But 
                we also understand that numbers alone don't tell the full story – that's why we evaluate user 
                experience, customer support quality, and the overall feel of each platform.
              </p>
              
              <p>
                Transparency is non-negotiable. We clearly explain our testing processes, disclose our affiliate 
                relationships, and never let commercial partnerships influence our editorial content. If a casino 
                has issues, we report them. If a bonus isn't worth claiming, we say so. Our reputation depends on 
                providing honest, actionable guidance that helps you make better decisions.
              </p>
              
              <p>
                We continuously update our guides as the industry evolves. New regulations, updated bonus terms, 
                improved payment methods, and emerging trends all get incorporated into our content. What you read 
                today reflects the current state of UK online gambling, not outdated information from months ago.
              </p>
              
              <p>
                Every guide is written with one goal: empowering you to play smarter. Whether you're a complete 
                beginner or a seasoned player, our content provides the insights you need to maximize value, avoid 
                pitfalls, and enjoy casino gaming responsibly.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-dark text-center mb-12">Essential Casino Guides</h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {guides.map((guide, index) => (
              <Link
                key={guide.id}
                to={`/guides/${guide.id}`}
                data-discover="true"
                className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-yellow-accent hover:shadow-lg transition-all transform hover:scale-105"
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-yellow-accent font-bold text-lg">0{index + 1}</span>
                  <ArrowRight className="text-gray-400" size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-dark mb-3">{guide.title}</h3>
                <p className="text-gray-light leading-relaxed">{guide.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-dark text-center mb-12">Industry Insights</h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Chart 1 */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center space-x-2 mb-6">
                <Shield className="text-yellow-accent" size={24} />
                <h3 className="text-xl font-bold text-gray-dark">UK Casino Licensing</h3>
              </div>
              <div className="space-y-4">
                {chart1Data.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-light">{item.label}</span>
                      <span className="text-sm font-bold text-gray-dark">{item.value}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className={`${item.color} h-4 rounded-full transition-all`}
                        style={{ width: `${item.value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-light mt-4">
                Percentage of UK casinos operating with proper licensing and regulation.
              </p>
            </div>

            {/* Chart 2 */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center space-x-2 mb-6">
                <TrendingUp className="text-yellow-accent" size={24} />
                <h3 className="text-xl font-bold text-gray-dark">Withdrawal Times</h3>
              </div>
              <div className="space-y-4">
                {chart2Data.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-light">{item.label}</span>
                      <span className="text-sm font-bold text-gray-dark">{item.value}h</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className={`${item.color} h-4 rounded-full transition-all`}
                        style={{ width: `${(item.value / 72) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-light mt-4">
                Average processing times for different withdrawal methods in hours.
              </p>
            </div>

            {/* Chart 3 */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center space-x-2 mb-6">
                <Award className="text-yellow-accent" size={24} />
                <h3 className="text-xl font-bold text-gray-dark">Game Popularity</h3>
              </div>
              <div className="space-y-4">
                {chart3Data.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-light">{item.label}</span>
                      <span className="text-sm font-bold text-gray-dark">{item.value}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className={`${item.color} h-4 rounded-full transition-all`}
                        style={{ width: `${item.value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-light mt-4">
                Player preference distribution across different game categories.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GuidesPage;

